import { Typography } from 'antd'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import Loading from '../shared/components/Loading'
import { fetchConfigs } from '../features/config/service/configService'
import { setAppConfigs } from '../redux/config/actions'
import { fetchEntities } from '../features/entities/service/entitiesService'
import { setEntities } from '../redux/entities/actions'
import RequestError from '../shared/components/RequestError'
import { currentUserSelector } from '../redux/session/currentUser/selectors'

type DataManagerProps = {
  children?: React.ReactNode
}

const { Text, Title } = Typography

const DataManager: React.FC<DataManagerProps> = ({ children }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const currentUser = useSelector(currentUserSelector)
  // Greet the user only on the load right after logging in, not on page reloads
  const [greet, setGreet] = useState<boolean>(() => !!location.state?.fromLogin)

  const handleData = async () => {
    try {
      setIsLoading(true)
      setError(null) // Reset error state

      const configs = await fetchConfigs()
      if (!configs) {
        throw new Error('Failed to load configurations')
      }
      dispatch(setAppConfigs(configs))

      const entities = await fetchEntities()
      if (!entities) {
        throw new Error('Failed to load entities')
      }
      dispatch(setEntities(entities))
    } catch (e) {
      console.error('Data loading error:', e)
      setError(e instanceof Error ? e.message : t('global:dataLoadError', 'Failed to load required data'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    handleData()
  }, [dispatch, t])

  useEffect(() => {
    if (!greet || isLoading) return
    setGreet(false)
    // Drop the login flag from history so a page reload doesn't greet again
    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null })
  }, [greet, isLoading, navigate, location.pathname, location.search, location.hash])

  if (isLoading) {
    return (
      <Loading>
        <div style={{ textAlign: 'center' }}>
          {greet && (
            <Title level={4} style={{ margin: '16px 0 4px' }}>
              {currentUser?.firstName
                ? t('global:welcome-back-name', { name: currentUser.firstName })
                : t('global:welcome-back')}
            </Title>
          )}
          <Text type="secondary">{t('global:loadingData')}</Text>
        </div>
      </Loading>
    )
  }

  if (error) {
    return (
      <RequestError
        error={error}
        refetch={() => {
          setIsLoading(true)
          setError(null)
          handleData()
        }}
      />
    )
  }

  return <div>{children}</div>
}

export default DataManager
